import { Box, Stack, Typography, useTheme } from "@mui/material";
import { DataTable, SelectField } from "components";
import {
  useGetAllCustomers,
  useGetIntegrationMappedCustomers,
  useGetNcentralCustomers,
  useMapIntegrationCustomer,
} from "controller";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import toast from "react-hot-toast";
import { useDebounce } from "utilz/helpers/hooks/useDebounceHook";
import { NCENTRAL_SKELETON_ROWS } from "./constants";

const TableHeaders = [
  {
    name: "N-able Customer",
    sortable: false,
    key: "external_customer_name",
  },
  {
    name: "DeskDay Customer",
    sortable: false,
    key: "ddCustomer",
  },
];

export const NcentralStep2 = forwardRef(
  ({ onStepChange }: { onStepChange: (step: number) => void }, ref) => {
    const theme = useTheme();
    const { mutateAsync: mapCustomer } = useMapIntegrationCustomer();
    // Debounced server-side search, as in Xero and QuickBooks: customers beyond
    // the loaded page stay reachable instead of filtering only what's loaded.
    const [customerSearch, setCustomerSearch] = useDebounce("", 300);
    const {
      data: customer,
      isLoading: isDeskDayCustomersLoading,
      isFetching: isDeskDayCustomersFetching,
    } = useGetAllCustomers({
      pageNumber: 1,
      // TODO: lower to a real page size once the API pages search results;
      // today `page` is ignored when `searchQuery` is set (UAT, 3 Nov 2026).
      perPageCount: 2000,
      sortColumn: "customer_name",
      sortDirection: "asc",
      filter: [{ column: "is_active", operator: "=", value: "true" }],
      searchQuery: customerSearch,
    });
    const { data: ncentralCustomers, isLoading: isNcentralCustomersLoading } =
      useGetNcentralCustomers();
    const {
      data: ncentralMappedCustomers,
      isLoading: isNcentralMappedCustomersLoading,
    } = useGetIntegrationMappedCustomers("ncentral");

    const [transformedCustomerData, setTransformedCustomerData] = useState<
      any[]
    >([]);

    // The rows are built in the effect below, a render after the queries
    // settle. Keep the skeleton up until they exist.
    const isBuildingRows =
      Boolean(ncentralCustomers?.length) &&
      Boolean(ncentralMappedCustomers) &&
      transformedCustomerData.length === 0;

    useEffect(() => {
      if (ncentralCustomers && ncentralMappedCustomers) {
        createMappingArray();
      }
    }, [ncentralCustomers, ncentralMappedCustomers]);

    const createMappingArray = () => {
      const customerArray = ncentralCustomers?.map(
        (customer: Record<string, any>) => {
          const customerIndex = ncentralMappedCustomers?.findIndex(
            (mappedCustomer: Record<string, any>) =>
              mappedCustomer.external_customer_id === customer.customerId,
          );
          const customerObject = {
            deskday_customer_id:
              customerIndex > -1
                ? ncentralMappedCustomers[customerIndex].deskday_customer_id
                : "",
            deskday_customer_name:
              customerIndex > -1
                ? ncentralMappedCustomers[customerIndex].deskday_customer_name
                : "",
            external_customer_id: customer.customerId,
            external_customer_name: customer.customerName,
          };
          return customerObject;
        },
      );
      setTransformedCustomerData(customerArray);
    };

    const addCustomerMapping = ({
      rowData,
      customerId,
      rowIndex,
    }: {
      rowData: any;
      customerId: string | null;
      rowIndex: number;
    }) => {
      if (!customerId) {
        setTransformedCustomerData((prev) => {
          const newPrev = [...prev];
          newPrev[rowIndex].deskday_customer_id = customerId;
          newPrev[rowIndex].deskday_customer_name = null;
          return newPrev;
        });
        return;
      }
      const customerIndex = customer?.result.findIndex(
        (customer: Record<string, any>) => customer.mapping_id === customerId,
      );

      setTransformedCustomerData((prev) => {
        const newPrev = [...prev];
        newPrev[rowIndex].deskday_customer_id = customerId;
        newPrev[rowIndex].deskday_customer_name =
          customer?.result[customerIndex].customer_name;
        return newPrev;
      });
    };
    const cellBuilders = {
      ddCustomer: (rowData: any, index?: number) => {
        return (
          <SelectField
            sx={{ width: "180px" }}
            name="customer"
            hideHelperBox
            options={customer?.result || []}
            isSearchable
            isLoading={isDeskDayCustomersFetching}
            onSearchValueChange={setCustomerSearch}
            // The backend already filtered by `searchQuery`; don't re-filter.
            onFilterValue={() => true}
            // While a search narrows the list, a row's customer may not be in
            // it, so fall back to the name held in the row.
            renderValue={(_value, optionValue) =>
              optionValue?.customer_name || rowData.deskday_customer_name || ""
            }
            size="small"
            value={rowData.deskday_customer_id}
            isResponsive={false}
            labelMappingKey="customer_name"
            valueMappingKey="mapping_id"
            searchFieldProps={{
              placeholder: "Search customer",
            }}
            onValueChange={(value) => {
              addCustomerMapping({
                rowData,
                customerId: value,
                rowIndex: index || 0,
              });
            }}
            isClearable
          ></SelectField>
        );
      },
    };

    const handleTransformForSubmit = async () => {
      const submitArray: Array<any> = [];
      transformedCustomerData?.map((customer: Record<string, any>) => {
        if (customer.deskday_customer_id) {
          submitArray.push({
            ...customer,
            external_customer_id: customer.external_customer_id,
            external_customer_name: customer.external_customer_name,
          });
        }
      });
      return submitArray;
    };
    const onSubmit = async () => {
      try {
        const mappedCustomers = await handleTransformForSubmit();

        await mapCustomer({
          customerData: mappedCustomers,
          source: "ncentral",
        });
        toast.success("Customer mapped successfully");

        return true;
        // onStepChange(3);
      } catch (error) {
        toast.error("Failed to map customers");
        return false;
      }
    };

    useImperativeHandle(ref, () => {
      return {
        onSubmit,
      };
    });
    return (
      <Stack sx={{ px: "32px", py: "24px", gap: "16px", height: "100%" }}>
        <Typography variant="h6">Customer mapping</Typography>
        <Typography variant="body-medium" color={theme.palette.lightBg.low}>
          Please map your customers with corresponding customer names in N-able
        </Typography>
        <Box
          sx={{
            flex: "1",
            width: "100%",
          }}
        >
          <DataTable
            isDataLoading={
              isNcentralCustomersLoading ||
              isNcentralMappedCustomersLoading ||
              // A new search term is a new query, so `isLoading` is true
              // again while it runs; only the first load blocks the table.
              (isDeskDayCustomersLoading && !customerSearch) ||
              isBuildingRows
            }
            headers={TableHeaders || []}
            data={transformedCustomerData || []}
            uniqueKey="external_customer_id"
            cellBuilders={cellBuilders}
            selectedItems={[]}
            currentPageNumber={0}
            itemsPerPage={NCENTRAL_SKELETON_ROWS}
            multiselect={false}
            containerStyles={{
              height: "100%",
              minHeight: "200px",
              paddingBottom: "58px",
            }}
            headerCellStyles={{
              textTransform: "none",
            }}
          />
        </Box>
      </Stack>
    );
  },
);
